import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { useDispatch, connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, Grid, Box, CircularProgress } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ReactSelect from '@components/Select';
import { FilterRoomDate } from '@src/constants/common';
import { getRoomBillsList } from '@utilities/order';
import { removeRoomBills } from '@utilities/auth';
import {
    buildRoomBillSnapshot,
    logOrderEventAsync,
} from '@utilities/orderLogging';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import GuestList from './GuestList';
import RoomBillingGuestDialog from './RoomBillingGuestDialog';
import {
    selectRoomBillingRestaurant,
    selectRoomBillingUser,
    selectRoomList,
    selectRoomBillingUserPermissions,
    selectRoomGSTSlab,
} from './roomBillingSelectors';

import '../Orders.scss';
import './RoomBilling.scss';

const RoomAvailabilityMatrix = React.lazy(() => import('./RoomAvailabilityMatrix'));

type GuestDialogState = {
    open: boolean;
    editOrderId: string | null;
    orderSnapshot?: Record<string, unknown> | null;
    prefilledRoomNumber?: string;
};

const RoomBilling = ({
    restaurant,
    user,
    roomList,
    roomGSTSlab,
    userPermissions,
}: any) => {
    const [guestDialog, setGuestDialog] = useState<GuestDialogState>({
        open: false,
        editOrderId: null,
    });
    const [roomMatrixOpen, setRoomMatrixOpen] = useState(false);
    const [days, setDays] = useState(FilterRoomDate[0].value);
    const [isCustomRange, setIsCustomRange] = useState(false);
    const [startDate, setStartDate] = useState<Date>(() => {
        const d = new Date();
        d.setDate(d.getDate() - 5);
        return d;
    });
    const [endDate, setEndDate] = useState<Date>(new Date());

    const rangeFilter = useMemo(
        () => ({
            type: 'range' as const,
            startDate,
            endDate,
        }),
        [startDate, endDate],
    );

    const listDaysFilter = isCustomRange ? rangeFilter : days;

    const params = useParams();
    const dispatch = useDispatch();

    const refreshRoomBillsList = useCallback(() => {
        if (isCustomRange && startDate && endDate) {
            getRoomBillsList('admin', params?.restaurantId, dispatch, {
                type: 'range',
                startDate,
                endDate,
            });
        } else {
            getRoomBillsList('admin', params?.restaurantId, dispatch, days);
        }
    }, [isCustomRange, startDate, endDate, days, params?.restaurantId, dispatch]);

    const handleClickOpen = useCallback(() => {
        setGuestDialog({ open: true, editOrderId: null, orderSnapshot: null });
    }, []);

    const handleClickOpenWithRoom = useCallback((opts?: { roomNumber?: string }) => {
        setGuestDialog({
            open: true,
            editOrderId: null,
            orderSnapshot: null,
            prefilledRoomNumber: opts?.roomNumber,
        });
    }, []);

    const handleGuestDialogClose = useCallback(() => {
        setGuestDialog({ open: false, editOrderId: null, orderSnapshot: null });
    }, []);

    const handleSelectedOrder = useCallback((order: any) => {
        if (!order?.orderId) {
            return;
        }
        setGuestDialog({
            open: true,
            editOrderId: order.orderId,
            orderSnapshot: order,
        });
    }, []);

    const handleRemoveRoomBills = useCallback(
        (orderOrId: any) => {
            const order =
                orderOrId && typeof orderOrId === 'object'
                    ? orderOrId
                    : { orderId: orderOrId };
            const orderId = order?.orderId;
            if (!orderId) return;

            const restaurantId =
                order?.restaurantId ||
                order?.restaurant?.docId ||
                params?.restaurantId ||
                '';
            const snapshot = buildRoomBillSnapshot(order);
            const clientRequestId =
                typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID()
                    : `room-del-${Date.now()}`;

            removeRoomBills(orderId).then((ok) => {
                logOrderEventAsync({
                    restaurantId: String(restaurantId || 'unknown'),
                    orderId,
                    collectionName: 'room-bills',
                    orderKind: 'room',
                    action: 'delete',
                    status: ok ? 'success' : 'failed',
                    source: 'room-billing',
                    errorMessage: ok ? null : 'Firestore delete failed or order id missing',
                    snapshot,
                    clientRequestId,
                });
                if (ok) {
                    refreshRoomBillsList();
                }
            });
        },
        [refreshRoomBillsList, params?.restaurantId],
    );

    const handleMatrixClose = useCallback(() => {
        setRoomMatrixOpen(false);
    }, []);

    const handleMatrixOpen = useCallback(() => {
        setRoomMatrixOpen(true);
    }, []);

    return (
        <>
            <div className="orderssPageWrap">
                <Box
                    style={{
                        backgroundColor: 'var(--primary-light)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 8,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <ReactSelect
                            key="room-billing-filter-days"
                            className="filterDays"
                            defaultValue={FilterRoomDate[0]}
                            options={FilterRoomDate}
                            onChange={(val: any) => {
                                if (val?.value === 'custom') {
                                    setIsCustomRange(true);
                                } else {
                                    setIsCustomRange(false);
                                    setDays(val?.value);
                                }
                            }}
                        />
                        {isCustomRange && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => {
                                        if (date) setStartDate(date);
                                    }}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    className="date-picker"
                                />
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => {
                                        if (date) setEndDate(date);
                                    }}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate}
                                    className="date-picker"
                                />
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            color="secondary"
                            aria-label="Availability"
                            sx={{
                                m: '0 !important',
                                minWidth: { xs: 44, sm: 'auto' },
                                px: { xs: 1, sm: undefined },
                            }}
                            className="btn"
                            variant="outlined"
                            onClick={handleMatrixOpen}
                        >
                            <CalendarMonthIcon
                                sx={{
                                    display: { xs: 'block', sm: 'none' },
                                    fontSize: '1.35rem',
                                }}
                            />
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                Availability
                            </Box>
                        </Button>
                        <Button
                            sx={{ m: '0 !important' }}
                            color="primary"
                            className="btn"
                            variant="outlined"
                            onClick={handleClickOpen}
                        >
                            + New Guest
                        </Button>
                    </Box>
                </Box>
            </div>
            {roomMatrixOpen && (
                <Suspense
                    fallback={
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    }
                >
                    <RoomAvailabilityMatrix
                        open={roomMatrixOpen}
                        onClose={handleMatrixClose}
                        restaurantId={params?.restaurantId}
                        roomList={roomList}
                        onEditOrder={handleSelectedOrder}
                        onNewGuest={handleClickOpenWithRoom}
                    />
                </Suspense>
            )}
            <Grid className="wrapMob" container spacing={2}>
                <Grid item xs={12}>
                    <GuestList
                        days={listDaysFilter}
                        onSelectOrder={handleSelectedOrder}
                        onRemoveOrder={handleRemoveRoomBills}
                    />
                </Grid>
            </Grid>
            <RoomBillingGuestDialog
                key={guestDialog.editOrderId ?? 'new-guest'}
                open={guestDialog.open}
                onClose={handleGuestDialogClose}
                onSaved={refreshRoomBillsList}
                editOrderId={guestDialog.editOrderId}
                orderSnapshot={guestDialog.orderSnapshot}
                prefilledRoomNumber={guestDialog.prefilledRoomNumber}
                restaurant={restaurant}
                user={user}
                roomList={roomList}
                roomGSTSlab={roomGSTSlab}
                userPermissions={userPermissions}
            />
        </>
    );
};

const mapStateToProps = (state: any) => ({
    restaurant: selectRoomBillingRestaurant(state),
    user: selectRoomBillingUser(state),
    roomList: selectRoomList(state),
    userPermissions: selectRoomBillingUserPermissions(state),
    roomGSTSlab: selectRoomGSTSlab(state),
});

export default connect(mapStateToProps)(RoomBilling);
